<template>
  <div
    ref="inputContainer"
    class="search-bar cr-w-100 cr-flex cr-align-center gap-2"
    :class="{
      [`search-bar--${theme}`]: true,
    }"
    @click="focusInput"
  >
    <BaseIconStack v-if="!inputHasFocus && !modelValue && theme === 'default'">
      <template #icons>
        <BaseIcon
          :font-size="'24px'"
          :icon="'fa-search'"
        />
        <BaseIcon
          class="sparkle-icon"
          :font-size="'7px'"
          :icon="'fa-sparkles'"
        />
      </template>
    </BaseIconStack>
    <div
      v-once
      ref="input"
      class="search-bar__input cr-w-100 body-l-regular"
      :class="`search-bar__input--${theme} ${ theme === 'default' ? 'body-l-regular' : 'body-s-regular'}`"
      contenteditable="false"
      data-testid="homepage-search-bar-input"
      :placeholder="searchPlaceholder"
      @blur="blurInput"
      @input="$emit('update:modelValue', $event.target.textContent)"
      @keydown.enter.prevent="submit"
      @keydown.esc.prevent="blurInput"
      @keydown.tab.prevent="blurInput"
      @paste="onPaste"
    >
      {{ modelValue }}
    </div>

    <BaseButton
      class="search-bar__submit"
      :disabled="!modelValue"
      emphasis="high"
      icon="fa-regular fa-paper-plane-top"
      size="compact"
      theme="mono"
      @click="submit"
    />
  </div>
</template>

<script>
import BaseButton from './BaseButton.vue'
import BaseIconStack from './BaseIconStack.vue'
import BaseIcon from './BaseIcon.vue'

const THEMES = ['default', 'lightweight']

export default {
  name: 'ClientHomePageSearchBar',
  components: {
    BaseButton,
    BaseIconStack,
    BaseIcon,
  },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    searchPlaceholder: {
      type: String,
      default: '',
    },
    theme: {
      type: String,
      default: 'default',
      validator: (value) => THEMES.includes(value),
    },
  },
  emits: ['submit', 'update:modelValue'],
  data() {
    return {
      inputHasFocus: false,
    }
  },
  watch: {
    modelValue(value) {
      if (document.activeElement === this.$refs.input) {
        return
      }
      this.$refs.input.innerHTML = value
    },
    searchPlaceholder(placeholder) {
      this.$refs.input.setAttribute('placeholder', placeholder)
    },
  },
  methods: {
    blurInput() {
      this.inputHasFocus = false
      this.$refs.input.setAttribute('contenteditable', false)

      if (!this.modelValue) {
        this.$refs.input.innerHTML = ''
      }
    },
    focusInput() {
      this.inputHasFocus = true
      this.$refs.input.setAttribute('contenteditable', true)
      this.$refs.input.focus()
    },
    onPaste(event) {
      event.preventDefault()
      const pastedText = event.clipboardData.getData('text/plain')
      this.$emit('update:modelValue', pastedText)
      this.$refs.input.innerHTML = pastedText

      const selection = window.getSelection()
      selection.selectAllChildren(this.$refs.input)
      selection.collapseToEnd()
    },
    submit() {
      this.$refs.input.innerHTML = ''
      this.$emit('submit')
    },
  },
}
</script>

<style lang="scss" scoped>
.sparkle-icon {
  position: absolute;
  right: 3px;
}

.search-bar {
  z-index: 1;
  cursor: text;
  margin-bottom: var(--space-8);
  transition: box-shadow .3s ease-in-out;

  &--lightweight {
    min-height: 36px;
    margin-bottom: 0;
    padding: var(--space-1) var(--space-3);
    border: double 2px transparent;
    border-radius: 8px;
    background-image: linear-gradient(var(--blueberry-50), var(--blueberry-50)),
      linear-gradient(to right, #B1B6E7, #646DCE);
    background-origin: border-box;
    background-clip: padding-box, border-box;
  }

  &--default {
    padding: var(--space-3);
    box-shadow: 0px 0px 24px 1px #B1B6E7;
    border: double 4px transparent;
    border-radius: 20px;
    background-image: linear-gradient(var(--blueberry-50), var(--blueberry-50)),
      linear-gradient(to right, #B1B6E7, #646DCE);
    background-origin: border-box;
    background-clip: padding-box, border-box;

    &:has(div:focus) {
      box-shadow: 0px 0px 16px 8px #B1B6E7;
    }

    :root:root &__submit {
      align-self: flex-end;
    }
  }

  &__input {
    border: none;
    background: var(--blueberry-50);
    transition: opacity 0.2s ease;
    overflow-y: auto;

    &--lightweight {
      max-height: 64px;
    }

    &--default {
      max-height: 112px;
    }

    &:focus {
      outline: none;
    }

    &:empty:before {
      content: attr(placeholder);
      color: currentColor;
    }

    &:empty:focus:before {
      content: "";
    }
  }

  :root:root &__submit {
    justify-content: center;
    background-color: var(--pepper-800);
    border-color: var(--pepper-800);
  }
}
</style>
